import { useState, useEffect } from 'react';
import { Plus, Search, FileText, Trash2, Edit, Eye } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { kbApi } from '../services/kb.api';
import { KB_CATEGORIES } from '../lib/constants';
import { fmtDate } from '../lib/formatters';
import { toast } from 'sonner';

export default function KnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [filters, setFilters] = useState({ search: '', category: '' });
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'sops', content: '', tags: '' });

  const load = () => kbApi.list(filters).then(r => setArticles(r.data));
  useEffect(() => { load(); }, [filters]);

  const open = async (id) => {
    const { data } = await kbApi.get(id);
    setSelected(data);
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await kbApi.create({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) });
      toast.success('Article created');
      setShowForm(false);
      setForm({ title: '', category: 'sops', content: '', tags: '' });
      load();
    } catch { toast.error('Failed to create article'); }
  };

  const remove = async (id) => {
    try {
      await kbApi.delete(id);
      setArticles(a => a.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="flex h-full gap-4">
      {/* Sidebar */}
      <div className="w-64 shrink-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-700">Knowledge Base</h2>
          <button onClick={() => setShowForm(s => !s)} className="p-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">
            <Plus size={14} />
          </button>
        </div>

        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input className="w-full pl-6 pr-2 py-1.5 text-xs border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
            placeholder="Search..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>

        <select className="text-xs border border-zinc-200 rounded px-2 py-1.5 focus:outline-none"
          value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
          <option value="">All Categories</option>
          {KB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <div className="space-y-1 overflow-y-auto flex-1">
          {articles.map(a => (
            <div key={a.id}
              onClick={() => open(a.id)}
              className={`flex items-start justify-between p-2 rounded cursor-pointer text-xs group ${selected?.id === a.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-zinc-50 border border-transparent'}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-zinc-800 truncate">{a.title}</p>
                <p className="text-zinc-400 mt-0.5">{a.category} · {fmtDate(a.updated_at)}</p>
              </div>
              <button onClick={e => { e.stopPropagation(); remove(a.id); }} className="opacity-0 group-hover:opacity-100 text-zinc-300 hover:text-red-500 ml-1">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {articles.length === 0 && <p className="text-xs text-zinc-400 p-2">No articles found.</p>}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 bg-white border border-zinc-200 rounded-lg overflow-hidden flex flex-col">
        {showForm ? (
          <form onSubmit={create} className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-zinc-700">New Article</h3>
            <input required className="w-full text-sm border border-zinc-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <select className="w-full text-sm border border-zinc-200 rounded px-2 py-1.5 focus:outline-none"
              value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {KB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className="w-full text-sm border border-zinc-200 rounded px-2 py-1.5 focus:outline-none"
              placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
            <textarea required rows={12}
              className="w-full text-sm border border-zinc-200 rounded px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Markdown content..." value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="text-xs text-zinc-500 hover:text-zinc-700 px-3 py-1.5">Cancel</button>
              <button type="submit" className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700">Save Article</button>
            </div>
          </form>
        ) : selected ? (
          <div className="p-4 overflow-y-auto">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">{selected.title}</h2>
                <p className="text-xs text-zinc-400 mt-0.5">{selected.category} · {selected.created_by_name} · {fmtDate(selected.updated_at)}</p>
              </div>
            </div>
            <div className="prose prose-sm prose-zinc max-w-none
              prose-headings:font-semibold prose-headings:text-zinc-900
              prose-h1:text-xl prose-h2:text-lg prose-h3:text-base
              prose-p:text-zinc-700 prose-p:leading-relaxed
              prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-zinc-900
              prose-code:bg-zinc-100 prose-code:text-indigo-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono
              prose-pre:bg-zinc-900 prose-pre:text-zinc-100 prose-pre:rounded-lg prose-pre:text-xs
              prose-blockquote:border-l-indigo-400 prose-blockquote:text-zinc-500
              prose-ul:text-zinc-700 prose-ol:text-zinc-700
              prose-li:marker:text-zinc-400
              prose-hr:border-zinc-200
              prose-table:text-sm prose-th:text-zinc-700 prose-td:text-zinc-600">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{selected.content}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-400">
            <div className="text-center">
              <FileText size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Select an article or create a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
